import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
 Trash2,
 Star,
 AlertCircle,
 Package,
 User,
 CheckCircle,
 XCircle,
 Loader2,
 ListChecks,
 Eye,
 ExternalLink,
} from 'lucide-react';
import { Button, SearchSelect, StatsGrid, PillToggle } from '../../../components/shared';
import DataTable from '../../../components/shared/DataTable';
import { useToastContext } from '../../../contexts/ToastContext';
import {
 useAdminVendorServices,
 useAdminVendorServiceStatistics,
 useToggleVendorServiceStatus,
 useToggleVendorServiceFeatured,
 useDeleteVendorService,
} from '../../../services/adminVendorServiceService';
import { useAdminVendors } from '../../../hooks/useAdminVendors';
import { AdminPageHeader, AdminGuideCallout } from '../../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../../utils/adminPageGuides';

const money = (n) => {
 const v = Number(n);
 if (Number.isNaN(v)) return '—';
 return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(v);
};

const AdminVendorServices = () => {
 const navigate = useNavigate();
 const [statusFilter, setStatusFilter] = useState('all');
 const [categoryFilter, setCategoryFilter] = useState('all');
 const [featuredFilter, setFeaturedFilter] = useState('all');
 const [vendorFilter, setVendorFilter] = useState('all');

 const { success, error: showError } = useToastContext();

 const queryParams = {
 is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
 is_featured: featuredFilter === 'all' ? undefined : featuredFilter === 'featured',
 service_category_id: categoryFilter === 'all' ? undefined : categoryFilter,
 vendor_id: vendorFilter === 'all' ? undefined : vendorFilter,
 sort_by: 'created_at',
 sort_order: 'desc',
 };

 const { data: vendorsResponse } = useAdminVendors({
 per_page: 500,
 sort_by: 'created_at',
 sort_order: 'desc',
 });

 const vendorOptions = useMemo(() => {
 const vendors = vendorsResponse?.data?.data || vendorsResponse?.data || [];
 if (!Array.isArray(vendors)) return [{ value: 'all', label: 'All vendors' }];

 const options = vendors
 .filter((v) => v && (v.vendor_profile || v.vendorProfile))
 .map((vendor) => {
 const vp = vendor.vendor_profile || vendor.vendorProfile;
 const name = vp?.business_name || vendor.name || 'Vendor';
 const city = vp?.city || '';
 const state = vp?.state || '';
 const loc = [city, state].filter(Boolean).join(', ');
 return {
 value: vp.id,
 label: loc ? `${name} — ${loc}` : name,
 };
 })
 .sort((a, b) => a.label.localeCompare(b.label));

 return [{ value: 'all', label: 'All vendors' }, ...options];
 }, [vendorsResponse]);

 const {
 data: servicesData,
 isLoading: loading,
 error: servicesError,
 refetch,
 } = useAdminVendorServices(queryParams);

 const { data: statisticsData, isLoading: statsLoading } = useAdminVendorServiceStatistics();

 const services = servicesData?.data || [];
 const statistics = statisticsData?.data;

 const toggleStatusMutation = useToggleVendorServiceStatus({
 onSuccess: (res) => success(res?.message || 'Service status updated'),
 onError: (error) => showError(error?.response?.data?.message || error.message || 'Failed to update status'),
 });

 const toggleFeaturedMutation = useToggleVendorServiceFeatured({
 onSuccess: (res) => success(res?.message || 'Featured status updated'),
 onError: (error) => showError(error?.response?.data?.message || error.message || 'Failed to update featured'),
 });

 const deleteServiceMutation = useDeleteVendorService({
 onSuccess: () => success('Service deleted'),
 onError: (error) => showError(error?.response?.data?.message || error.message || 'Failed to delete service'),
 });

 const isStatusToggling = (serviceId) =>
 toggleStatusMutation.isPending && toggleStatusMutation.variables === serviceId;

 const isFeaturedToggling = (serviceId) =>
 toggleFeaturedMutation.isPending && toggleFeaturedMutation.variables === serviceId;

 const openDetails = (service) => {
 if (!service?.id) return;
 navigate(`/admin/services/${service.id}`);
 };

 const columns = [
 {
 key: 'service_name',
 label: 'Service',
 sortable: true,
 searchable: true,
 render: (service) => (
 <button
 type="button"
 className="flex items-center text-left w-full min-w-0"
 onClick={() => openDetails(service)}
 >
 <div className="h-10 w-10 rounded-xl bg-gray-900 flex items-center justify-center shrink-0">
 <Package className="h-5 w-5 text-white" />
 </div>
 <div className="ml-3 min-w-0">
 <div className="text-sm font-semibold text-ink hover:text-ink truncate">
 {service.service_name}
 </div>
 <div className="text-xs text-ink-tertiary truncate max-w-[16rem]">
 {service.description || 'No description'}
 </div>
 </div>
 </button>
 ),
 },
 {
 key: 'vendor',
 label: 'Vendor',
 sortable: false,
 searchable: true,
 render: (service) => {
 const userId = service.vendor_profile?.user_id || service.vendor_profile?.user?.id;
 return (
 <div className="flex items-center min-w-0">
 <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
 <User className="h-4 w-4 text-ink-secondary" />
 </div>
 <div className="ml-2.5 min-w-0">
 <div className="text-sm font-medium text-ink truncate">
 {service.vendor_profile?.business_name ||
 service.vendor_profile?.user?.name ||
 'Unknown'}
 </div>
 <div className="text-xs text-gray-500 truncate">
 {service.service_category?.category_name || 'Uncategorized'}
 </div>
 {userId ? (
 <Link
 to={`/admin/vendors/${userId}`}
 onClick={(e) => e.stopPropagation()}
 className="inline-flex items-center gap-1 text-[11px] text-ink hover:underline mt-0.5"
 >
 Vendor profile <ExternalLink className="w-3 h-3" />
 </Link>
 ) : null}
 </div>
 </div>
 );
 },
 },
 {
 key: 'base_price',
 label: 'Price',
 sortable: true,
 render: (service) => (
 <button
 type="button"
 className="text-sm font-semibold tabular-nums text-ink"
 onClick={() => openDetails(service)}
 >
 {money(service.base_price)}
 </button>
 ),
 },
 {
 key: 'is_active',
 label: 'Active',
 sortable: true,
 render: (service) => (
 <div onClick={(e) => e.stopPropagation()}>
 <PillToggle
 checked={Boolean(service.is_active)}
 onChange={() => toggleStatusMutation.mutate(service.id)}
 loading={isStatusToggling(service.id)}
 variant="success"
 onLabel="On"
 offLabel="Off"
 ariaLabel={`${service.service_name} active status`}
 />
 </div>
 ),
 },
 {
 key: 'is_featured',
 label: 'Featured service',
 sortable: true,
 render: (service) => (
 <div onClick={(e) => e.stopPropagation()} className="space-y-1">
 <PillToggle
 checked={Boolean(service.is_featured)}
 onChange={() => toggleFeaturedMutation.mutate(service.id)}
 loading={isFeaturedToggling(service.id)}
 variant="amber"
 onLabel="Yes"
 offLabel="No"
 ariaLabel={`${service.service_name} featured service`}
 />
 {service.is_featured ? (
 <p className="text-[10px] text-amber-700 flex items-center gap-0.5">
 <Star className="w-3 h-3" /> Shows in featured pool
 </p>
 ) : null}
 </div>
 ),
 },
 ];

 const actions = [
 {
 label: 'View',
 icon: Eye,
 onClick: (service) => openDetails(service),
 title: 'Open service details',
 variant: 'secondary',
 size: 'sm',
 },
 {
 label: 'Delete',
 icon: Trash2,
 onClick: (service) => {
 if (window.confirm('Delete this service? This cannot be undone.')) {
 deleteServiceMutation.mutate(service.id);
 }
 },
 title: 'Delete service',
 variant: 'danger',
 size: 'sm',
 },
 ];

 if (servicesError) {
 return (
 <div className="space-y-5">
 <div className="rounded-lg border border-red-200 bg-red-50/80 p-8 text-center">
 <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
 <h3 className="text-lg font-medium text-red-800 mb-2">Error loading services</h3>
 <p className="text-red-600 text-sm mb-6">
 {servicesError.message || 'Failed to load vendor services'}
 </p>
 <Button className="rounded-md" onClick={() => refetch()}>
 Try again
 </Button>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Vendor services"
 subtitle="Manage marketplace offerings. Featured service is separate from Featured vendor on the vendor profile."
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.services.title}
 steps={[
 ...(ADMIN_PAGE_GUIDES.services.steps || []),
 'Open a row to manage details — the old popup modal is gone.',
 'Featured service → browse badges + homepage featured pool. Featured vendor → vendor profile homepage spotlight.',
 ]}
 icon={ListChecks}
 />

 {statistics && !statsLoading && (
 <StatsGrid
 stats={[
 {
 key: 'total',
 title: 'Total services',
 value: statistics.total_services,
 icon: Package,
 variant: 'default',
 },
 {
 key: 'active',
 title: 'Active',
 value: statistics.active_services,
 icon: CheckCircle,
 variant: 'success',
 },
 {
 key: 'inactive',
 title: 'Inactive',
 value: statistics.inactive_services,
 icon: XCircle,
 variant: 'danger',
 },
 {
 key: 'featured',
 title: 'Featured services',
 value: statistics.featured_services,
 icon: Star,
 variant: 'warning',
 },
 ]}
 columns={4}
 />
 )}

 <div className="product-panel overflow-hidden">
 <div className="p-4 sm:p-5 space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted/60 p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <SearchSelect
 label="Vendor"
 value={vendorFilter}
 onChange={setVendorFilter}
 options={vendorOptions}
 placeholder="All vendors"
 searchable
 size="md"
 />
 <SearchSelect
 label="Active"
 value={statusFilter}
 onChange={setStatusFilter}
 options={[
 { value: 'all', label: 'All' },
 { value: 'active', label: 'Active' },
 { value: 'inactive', label: 'Inactive' },
 ]}
 searchable={false}
 size="md"
 />
 <SearchSelect
 label="Featured service"
 value={featuredFilter}
 onChange={setFeaturedFilter}
 options={[
 { value: 'all', label: 'All' },
 { value: 'featured', label: 'Featured only' },
 { value: 'regular', label: 'Not featured' },
 ]}
 searchable={false}
 size="md"
 />
 <SearchSelect
 label="Category"
 value={categoryFilter}
 onChange={setCategoryFilter}
 options={[{ value: 'all', label: 'All categories' }]}
 searchable={false}
 size="md"
 />
 </div>
 </div>

 {loading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading vendor services…</p>
 </div>
 ) : (
 <DataTable
 data={services}
 columns={columns}
 actions={actions}
 loading={false}
 searchable
 filterable={false}
 sortable
 selectable={false}
 pageSize={15}
 showPagination
 searchPlaceholder="Search services…"
 emptyMessage="No vendor services found."
 className="!border-0 !shadow-none"
 onRowClick={openDetails}
 />
 )}
 </div>
 </div>
 </div>
 );
};

export default AdminVendorServices;
